﻿import DomElementsBase from "@grindex-common/abstracts/DomElementsBase.class";
import ModuleElementsBase from "@grindex-common/abstracts/ModuleElementsBase.class";
import { getNextSibling } from "@grindex-common/helpers/domManipulation";
import { $elementChildren } from "@grindex-common/helpers/elements";
import { classes } from "@grindex-common/helpers/cssGlobals";
import { underscoreTemplateGrindex } from "@grindex-client/common/js/helpers/underscore-helper";

const SELECTOR_VARIANTS_CONTAINER = "#cds-feed-variants";
const SELECTOR_CRUMBS = "#cds-crumbs";
const SELECTOR_PRODUCT_RESULT = "#cds-feed-productresult";
const SELECTOR_COLLAPSIBLE = ".facet-group-header";
const SELECTOR_FACETS = "#cds-facets";
const SELECTOR_IMAGE_PRODUCT_LINK = ".cds-feed-product-link-container";
const SELECTOR_SEARCH_IMAGE = ".cds-feed-searchImage";
const SELECTOR_DOWNLOAD_LINKS = ".cds-feed-document a";

const ELEMENTS_FACET = "cds-facet";
const ELEMENT_SHOW_MORE_BUTTON = "show-more-button";
const ELEMENT_CHILD_PRODUCT_LINK = "cds-product-link";

const CLASS_GROUP = "group";
const CLASS_ICON_CLOSED = "icon-closed";
const CLASS_ICON_OPEN = "icon-open";

export default class CDSFeedDom extends DomElementsBase {
	constructor($module) {
		const selectors = [
			SELECTOR_COLLAPSIBLE,
			SELECTOR_CRUMBS,
			SELECTOR_FACETS,
			SELECTOR_IMAGE_PRODUCT_LINK,
			SELECTOR_PRODUCT_RESULT,
			SELECTOR_SEARCH_IMAGE,
			SELECTOR_VARIANTS_CONTAINER,
			SELECTOR_DOWNLOAD_LINKS,
		];

		super($module, selectors);

		const productDocumentsTemplate = document.getElementById("product-documents-template").innerHTML;
		this.productDocumentsTemplate = underscoreTemplateGrindex(productDocumentsTemplate);

		const facetsTemplate = document.getElementById("product-facets-template").innerHTML;
		this.facetsTemplate = underscoreTemplateGrindex(facetsTemplate);

		const productTemplate = document.getElementById("product-template").innerHTML;
		this.productTemplate = underscoreTemplateGrindex(productTemplate);

		const parentTemplate = document.getElementById("product-parent-template").innerHTML;
		this.parentTemplate = underscoreTemplateGrindex(parentTemplate);

		this.moduleElements = new ModuleElementsBase(this.$module);
	}

	getFacets($container) {
		if ($container) {
			return $elementChildren($container, ELEMENTS_FACET);
		} else {
			return this.moduleElements._childElements(ELEMENTS_FACET);
		}
	}

	getShowMoreButton() {
		return this.moduleElements._childElement(ELEMENT_SHOW_MORE_BUTTON);
	}

	getVariantsContainer() {
		return this.getElement(SELECTOR_VARIANTS_CONTAINER);
	}

	getCollapsibles() {
		return this.getElements(SELECTOR_COLLAPSIBLE, true);
	}

	getCrumbsContainer() {
		return this.getElement(SELECTOR_CRUMBS);
	}

	getFacetsContainer() {
		return this.getElement(SELECTOR_FACETS);
	}

	getImageProductLinks() {
		return this.getElements(SELECTOR_IMAGE_PRODUCT_LINK, true);
	}

	refreshProductLinks() {
		this.moduleElements._childElements(ELEMENT_CHILD_PRODUCT_LINK, true);
	}

	getProductLinks() {
		return this.moduleElements._childElements(ELEMENT_CHILD_PRODUCT_LINK);
	}

	getProductResultContainer() {
		return this.getElement(SELECTOR_PRODUCT_RESULT);
	}

	getSearchImage() {
		return this.getElement(SELECTOR_SEARCH_IMAGE);
	}

	appendProductResultMarkup(data) {
		this.getVariantsContainer().innerHTML = this.getVariantsContainer().innerHTML + this.productTemplate(data);
	}

	appendRootResultMarkup(data) {
		this.getVariantsContainer().innerHTML = this.getVariantsContainer().innerHTML + this.parentTemplate(data);
	}

	appendBreadCrumb(crumb, result) {
		const $crumbDiv = document.createElement("div");

		if (crumb === result.crumbs[result.crumbs.length - 1]) {
			const crumbSpan = `<span>${crumb.label}</a>`;
			$crumbDiv.innerHTML = crumbSpan;
		} else {
			const crumbLink = `<a xyl-element="cds-crumb-link" href="#" data-crumbid="${crumb.id}">${crumb.label}</a>`;
			$crumbDiv.innerHTML = crumbLink;
		}

		this.getCrumbsContainer().appendChild($crumbDiv);
	}

	clearProductResult() {
		this.getProductResultContainer().innerHTML = "";
	}

	setProductResultMarkup(data) {
		this.getProductResultContainer().innerHTML = this.productDocumentsTemplate(data);
		this.getProductResultContainer().scrollIntoView(true);
	}

	clearBreadCrumbs() {
		this.getCrumbsContainer().innerHTML = "";
	}

	hideFacetsContainer() {
		this.getFacetsContainer().classList.add(classes.HIDDEN);
	}

	showFacetsContainer() {
		this.getFacetsContainer().classList.remove(classes.HIDDEN);
	}

	setFacetContainerMarkup(result) {
		this.getFacetsContainer().innerHTML = this.facetsTemplate(result);
		return this.moduleElements._childElements(ELEMENTS_FACET, true);
	}

	hideSearchImage() {
		this.getSearchImage().classList.add(classes.HIDDEN);
	}

	showSearchImage() {
		this.getSearchImage().classList.remove(classes.HIDDEN);
	}

	setSearchImageSource(src) {
		this.getSearchImage().src = src;
	}

	toggleFilter(event) {
		const $obj = event.currentTarget;
		const $outerGroup = getNextSibling(event.currentTarget, `.${CLASS_GROUP}`);

		if ($obj.nextElementSibling !== 0) {
			$obj.classList.toggle(CLASS_ICON_OPEN);
			$obj.classList.toggle(CLASS_ICON_CLOSED);
			$outerGroup.classList.toggle(classes.HIDDEN);
		}
	}

	createDocumentLink(path) {
		const documentLink = document.createElement("a");
		documentLink.setAttribute("href", path);
		documentLink.setAttribute("download", path);

		return documentLink;
	}

	showMoreButtonIsEnabled() {
		return !this.getShowMoreButton().classList.contains(classes.DISABLED);
	}

	enableShowMoreButton() {
		this.getShowMoreButton().classList.remove(classes.DISABLED);
	}

	disableShowMoreButton() {
		this.getShowMoreButton().classList.add(classes.DISABLED);
	}

	hideShowMoreButton() {
		this.getShowMoreButton().classList.add(classes.HIDDEN);
	}

	showShowMoreButton() {
		this.getShowMoreButton().classList.remove(classes.HIDDEN);
	}

	_setFacetState($facet, checked) {
		const $childDiv = $facet.querySelector("div");
		const $childInput = $facet.querySelector("input");

		if (checked) {
			$childDiv.classList.add("checked");
			$childInput.checked = true;
		} else {
			$childDiv.classList.remove("checked");
			$childInput.checked = false;
		}
	}

	toggleFacetState($facet) {
		$facet.dataset.checked = $facet.dataset.checked === "true" ? "false" : "true";
		this._setFacetState($facet, $facet.dataset.checked === "true");
	}

	toggleDownloadLinks() {
		const links = this.getElements(SELECTOR_DOWNLOAD_LINKS, true);
		links.forEach(function (elem) {
			elem.classList.toggle(classes.DISABLED);
		});
	}
}
